import { useContext, useEffect, useMemo, useState } from 'react'
import { DeckContext } from './SlideDeck'
import { KcRegistryContext } from './KcRegistryContext'
import { emitKcAttemptEvent, getKcAttempts, recordKcAttempt } from '../lib/kcAttempts'
import { api } from '../lib/api'

export interface InteractiveKnowledgeCheckOption {
  id: string
  text: string
  correct?: boolean
}

export interface InteractiveKnowledgeCheckProps {
  question: string
  options: InteractiveKnowledgeCheckOption[]
  explanation?: string
  id?: string
}

function fnv1aHex(input: string): string {
  let hash = 0x811c9dc5
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i)
    hash = (hash + ((hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24))) >>> 0
  }
  return hash.toString(16).padStart(8, '0')
}

interface ChoiceState {
  choice: string | null
  revealed: boolean
}

export default function InteractiveKnowledgeCheck({
  question,
  options,
  explanation,
  id,
}: InteractiveKnowledgeCheckProps) {
  const kcId = useMemo(() => id ?? fnv1aHex(question), [id, question])
  const deck = useContext(DeckContext)
  const registry = useContext(KcRegistryContext)
  const moduleId = deck?.moduleId

  const [state, setState] = useState<ChoiceState>(() => {
    if (!moduleId) return { choice: null, revealed: false }
    const prior = getKcAttempts(moduleId)[kcId]
    if (prior) return { choice: prior.choiceId, revealed: true }
    return { choice: null, revealed: false }
  })

  useEffect(() => {
    registry?.register(kcId)
  }, [registry, kcId])

  function handleSelect(optionId: string) {
    if (state.revealed) return
    setState({ choice: optionId, revealed: false })
  }

  function handleSubmit() {
    if (!state.choice || state.revealed) return
    const chosen = options.find((o) => o.id === state.choice)
    const correct = Boolean(chosen?.correct)
    if (moduleId) {
      const { isFirst } = recordKcAttempt(moduleId, kcId, state.choice, correct)
      emitKcAttemptEvent(moduleId, kcId)
      if (isFirst) {
        api
          .submitKnowledgeCheckAttempt({
            module_id: moduleId,
            kc_id: kcId,
            choice_id: state.choice,
            correct,
          })
          .catch((err) => {
            console.warn('[kc] failed to persist attempt to server', err)
          })
      }
    }
    setState({ choice: state.choice, revealed: true })
  }

  return (
    <div className="slide-knowledge-check kc-interactive" data-archetype="knowledge-check">
      <div className="kc-interactive__header">
        <span className="kc-interactive__badge">Knowledge Check</span>
      </div>
      <p className="kc-interactive__question">{question}</p>
      <ol className="kc-interactive__options">
        {options.map((opt) => {
          const isChosen = state.choice === opt.id
          const showCorrect = state.revealed && opt.correct
          const showIncorrect = state.revealed && isChosen && !opt.correct
          const className = [
            'kc-interactive__option',
            isChosen ? 'is-chosen' : '',
            showCorrect ? 'is-correct' : '',
            showIncorrect ? 'is-incorrect' : '',
          ]
            .filter(Boolean)
            .join(' ')
          return (
            <li key={opt.id} className={className}>
              <button
                type="button"
                className="kc-interactive__option-button"
                onClick={() => handleSelect(opt.id)}
                disabled={state.revealed}
                aria-pressed={isChosen}
              >
                <span className="kc-interactive__option-text">{opt.text}</span>
                {showCorrect ? <span className="kc-interactive__mark"> ✓</span> : null}
                {showIncorrect ? <span className="kc-interactive__mark"> ✗</span> : null}
              </button>
            </li>
          )
        })}
      </ol>
      {!state.revealed ? (
        <button
          type="button"
          className="kc-interactive__submit"
          onClick={handleSubmit}
          disabled={!state.choice}
        >
          Submit
        </button>
      ) : null}
      {state.revealed && explanation ? (
        <div className="kc-interactive__explanation">
          <strong>Explanation:</strong> {explanation}
        </div>
      ) : null}
    </div>
  )
}
